import type { ComponentProps, FC, FocusEventHandler, ReactNode } from 'react';
import { FormField, InputGroup, Paragraph } from '@george-labs.com/design-system';
import type { ColSize } from '@george-labs.com/design-system/types/components/Layout';
import type { AmountObject } from '@george-labs.com/design-system/types/components/Input/AmountInput';
import type { FORM_FIELD_VARIANT } from '@george-labs.com/design-system/types/components/Form';

import { useField } from '@luna/form2';
import { LocalizedAmountInput, getCurrencySymbol } from '@shared/core-ui-amount';

import type { FormFieldProps } from './types/FormFieldProps';
import { shouldShowError } from './FieldErrorVisibilityService';
import {
    convertToNumberOrNull,
    fallbackToEmptyString,
    pickAmountValue,
} from './FormAmountFieldService';

interface Props extends FormFieldProps {
    currency?: string;
    maxLength?: number;
    labelColumnWidth?: ColSize;
    warning?: ReactNode;
    required?: boolean;
    precision?: number;
    prepend?: ReactNode;
    append?: ReactNode;
    disabled?: boolean;
    inputProps?: Partial<
        Omit<
            ComponentProps<typeof LocalizedAmountInput>,
            'value' | 'precision' | 'currency' | 'maxLength' | 'placeholder'
        >
    >;
    variant?: FORM_FIELD_VARIANT;
    isErrorVisible?: boolean;
    info?: ReactNode;
    alert?: ReactNode;
    onBeforeFormat?: (processedValue: string, amountObject: AmountObject) => string | number | null;
    shouldClearOnUnmount?: boolean;
}

interface Statics {
    LABEL_POSITION: (typeof FormField)['LABEL_POSITION'];
    VARIANT: (typeof FormField)['VARIANT'];
    ON_BEFORE_FORMAT: {
        PICK_AMOUNT_VALUE: Props['onBeforeFormat'];
        FALLBACK_TO_EMPTY_STRING: Props['onBeforeFormat'];
        CONVERT_TO_NUMBER_OR_NULL: Props['onBeforeFormat'];
    };
}

const AMOUNT_MAX_LENGTH = 12;
const LABEL_COLUMN_WIDTH = 4;

const FormAmountField: FC<Props> = ({
    className,
    required = true,
    label,
    currency,
    'data-cy': dataCy,
    maxLength = AMOUNT_MAX_LENGTH,
    labelColumnWidth = LABEL_COLUMN_WIDTH,
    fieldName,
    warning,
    labelPosition,
    variant,
    placeholder,
    description,
    precision,
    disabled,
    append,
    prepend,
    inputProps,
    isErrorVisible = true,
    addon,
    info,
    alert,
    onBeforeFormat = pickAmountValue,
    shouldClearOnUnmount,
    showErrorBeforeBlur,
    compact,
}) => {
    const [{ value, onBlur }, { error, touched, dirty }, { setValue, setTouched }] = useField<
        string | number | null
    >(fieldName, { shouldClearOnUnmount });

    const isFieldErrorVisible = shouldShowError(
        isErrorVisible,
        touched,
        error,
        showErrorBeforeBlur,
        dirty,
    );

    const handleAmountChange = (processedValue: string, amountObject: AmountObject) => {
        setValue(onBeforeFormat(processedValue, amountObject));
        setTouched();
        inputProps?.onFormat?.(processedValue, amountObject);
    };

    const handleBlur: FocusEventHandler<HTMLInputElement> = (e) => {
        onBlur(e);
        inputProps?.onBlur?.(e);
    };

    const handleFocus: FocusEventHandler<HTMLInputElement> = (e) => {
        e.target.select();
        inputProps?.onFocus?.(e);
    };

    return (
        <FormField
            className={className}
            required={required}
            input={
                <InputGroup disabled={disabled} append={append} prepend={prepend}>
                    <LocalizedAmountInput
                        {...inputProps}
                        value={value ?? ''}
                        onBlur={handleBlur}
                        precision={precision}
                        data-cy={dataCy}
                        currency={getCurrencySymbol(currency) || currency}
                        maxLength={maxLength}
                        placeholder={placeholder || ''}
                        onFocus={handleFocus}
                        onFormat={handleAmountChange}
                    />
                </InputGroup>
            }
            label={label}
            placeholder={placeholder}
            error={
                isFieldErrorVisible && <Paragraph data-cy={`${dataCy}-error`}>{error}</Paragraph>
            }
            labelColumnWidth={labelColumnWidth}
            warning={warning}
            labelPosition={labelPosition}
            description={description}
            variant={variant}
            info={info}
            alert={alert}
            addon={addon}
            compact={compact}
        />
    );
};

const FormAmountFieldWithStatics = FormAmountField as FC<Props> & Statics;

FormAmountFieldWithStatics.LABEL_POSITION = FormField.LABEL_POSITION;
FormAmountFieldWithStatics.VARIANT = FormField.VARIANT;
FormAmountFieldWithStatics.ON_BEFORE_FORMAT = {
    PICK_AMOUNT_VALUE: pickAmountValue,
    FALLBACK_TO_EMPTY_STRING: fallbackToEmptyString,
    CONVERT_TO_NUMBER_OR_NULL: convertToNumberOrNull,
};

export { FormAmountFieldWithStatics as FormAmountField };
